import { useState } from "react";
import { createAdminUser, listAdminUsers, listSites, updateAdminUser } from "./api";
import { SortTh } from "./ListControls";
import { nextSort, type SortState } from "./list-sort";
import { usePaginatedResource } from "./use-paginated-resource";
import { useAdminResource } from "./use-admin-resource";
import { EmptyRow } from "../dashboard/ui";
import { Pagination } from "../../components/Pagination";
import { SkeletonRows } from "../../components/Skeleton";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";

const BACKEND_ROLES = ["ADMIN", "HR", "SECURITY", "HOST"] as const;

type CreateUserForm = {
  username: string;
  email: string;
  password: string;
  role: string;
  isActive: boolean;
  siteId: string; // "" = company-wide (all sites)
};

const INITIAL_CREATE_FORM: CreateUserForm = {
  username: "",
  email: "",
  password: "",
  role: "SECURITY",
  isActive: true,
  siteId: "",
};

type EditUserForm = {
  email: string;
  role: string;
  isActive: boolean;
  password: string;
  siteId: string; // "" = company-wide (all sites)
};

export function UsersPanel() {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [sort, setSort] = useState<SortState | null>(null);
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const isActive = activeFilter === "all" ? undefined : activeFilter === "active";
  const onSort = (col: string) => setSort((current) => nextSort(current, col));

  const { data: sites } = useAdminResource(() => listSites(), []);
  const activeSites = (sites ?? []).filter((site) => site.is_active);
  const siteName = (siteId: number | null | undefined) =>
    siteId == null ? "All sites" : sites?.find((s) => s.id === siteId)?.name ?? `Site #${siteId}`;
  // Options for a site dropdown: active sites, plus the currently-selected one
  // even if it's since been deactivated -- otherwise editing a user assigned to
  // a now-inactive site would silently drop (or fail to show) their assignment.
  const siteOptions = (selectedId: string) => {
    const opts = [...activeSites];
    if (selectedId && !opts.some((s) => String(s.id) === selectedId)) {
      const current = sites?.find((s) => String(s.id) === selectedId);
      if (current) opts.push(current);
    }
    return opts;
  };

  const { rows, loading, error, page, hasNext, refresh, nextPage, prevPage } = usePaginatedResource(
    (limit, offset) =>
      listAdminUsers({
        search: search.trim() || undefined,
        role: roleFilter || undefined,
        is_active: isActive,
        sort_by: sort?.by,
        sort_dir: sort?.dir,
        limit,
        offset,
      }),
    [search, roleFilter, activeFilter, sort],
  );

  const [createForm, setCreateForm] = useState<CreateUserForm>(INITIAL_CREATE_FORM);
  const [createPending, setCreatePending] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({ email: "", role: "", isActive: true, password: "", siteId: "" });
  const [editPending, setEditPending] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function submitCreate() {
    if (!createForm.username.trim()) {
      setCreateError("Username is required");
      return;
    }
    if (createForm.password.length < 8) {
      setCreateError("Password must be at least 8 characters");
      return;
    }

    setCreatePending(true);
    setCreateError(null);
    try {
      const created = await createAdminUser({
        username: createForm.username.trim(),
        email: createForm.email.trim() || undefined,
        password: createForm.password,
        role: createForm.role,
        is_active: createForm.isActive,
        site_id: createForm.siteId ? Number(createForm.siteId) : null,
      });
      showSuccess(`Created user "${created.username}" (${created.role})`);
      setCreateForm(INITIAL_CREATE_FORM);
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to create user";
      setCreateError(message);
      showError(message);
    } finally {
      setCreatePending(false);
    }
  }

  function startEdit(
    userId: number,
    currentEmail: string,
    currentRole: string,
    currentActive: boolean,
    currentSiteId: number | null | undefined,
  ) {
    setEditingUserId(userId);
    setEditForm({
      email: currentEmail,
      role: currentRole,
      isActive: currentActive,
      password: "",
      siteId: currentSiteId == null ? "" : String(currentSiteId),
    });
    setEditError(null);
  }

  async function submitEdit(userId: number, username: string, wasActive: boolean) {
    if (wasActive && !editForm.isActive) {
      const ok = await confirm({
        title: "Deactivate user?",
        message: `"${username}" will no longer be able to sign in.`,
        confirmLabel: "Deactivate",
        danger: true,
      });
      if (!ok) return;
    }

    setEditPending(true);
    setEditError(null);
    try {
      await updateAdminUser(userId, {
        email: editForm.email.trim() || undefined,
        role: editForm.role,
        is_active: editForm.isActive,
        password: editForm.password.trim() || undefined,
        // "" means company-wide -> clear the assignment; otherwise assign.
        ...(editForm.siteId ? { site_id: Number(editForm.siteId) } : { clear_site: true }),
      });
      showSuccess(`Updated "${username}"`);
      setEditingUserId(null);
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to update user";
      setEditError(message);
      showError(message);
    } finally {
      setEditPending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>User Management</h2>
        <span>{rows.length} rows</span>
      </header>

      <form
        className="action-form action-card"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCreate();
        }}
      >
        <label htmlFor="admin-user-username">Create User</label>
        <div className="checkin-fields">
          <input
            id="admin-user-username"
            value={createForm.username}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, username: event.target.value }))}
            placeholder="Username *"
            aria-label="Username"
          />
          <input
            type="email"
            value={createForm.email}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
            placeholder="Email (optional)"
            aria-label="Email"
          />
          <input
            type="password"
            value={createForm.password}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, password: event.target.value }))}
            placeholder="Password (min 8 chars) *"
            aria-label="Password"
          />
          <select
            className="search-input"
            value={createForm.role}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, role: event.target.value }))}
            aria-label="Role"
          >
            {BACKEND_ROLES.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <select
            className="search-input"
            value={createForm.siteId}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, siteId: event.target.value }))}
            aria-label="Site"
          >
            <option value="">All sites (company-wide)</option>
            {activeSites.map((site) => (
              <option key={site.id} value={String(site.id)}>
                {site.name}
              </option>
            ))}
          </select>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={createForm.isActive}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, isActive: event.target.checked }))}
            />
            Active
          </label>
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={createPending}>
          {createPending ? "Creating..." : "Create User"}
        </button>
      </form>
      {createError ? <p className="error-banner action-error">{createError}</p> : null}

      <div className="filter-row">
        <input
          className="search-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by username"
          aria-label="Search users"
        />
        <select
          className="search-input"
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          aria-label="Filter by role"
        >
          <option value="">All roles</option>
          {BACKEND_ROLES.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        <select
          className="search-input"
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as "all" | "active" | "inactive")}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <SortTh label="Username" col="username" sort={sort} onSort={onSort} />
              <th>Email</th>
              <SortTh label="Role" col="role" sort={sort} onSort={onSort} />
              <th>Site</th>
              <SortTh label="Status" col="active" sort={sort} onSort={onSort} />
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={4} colSpan={6} />
            ) : rows.length === 0 ? (
              <EmptyRow label="No users found" colSpan={6} />
            ) : (
              rows.map((user) =>
                editingUserId === user.id ? (
                  <tr key={`user-edit-${user.id}`}>
                    <td colSpan={6}>
                      <div className="checkin-fields">
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, email: event.target.value }))}
                          placeholder="Email (optional)"
                          aria-label="Edit email"
                        />
                        <select
                          className="search-input"
                          value={editForm.role}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, role: event.target.value }))}
                          aria-label="Edit role"
                        >
                          {BACKEND_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <select
                          className="search-input"
                          value={editForm.siteId}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, siteId: event.target.value }))}
                          aria-label="Edit site"
                        >
                          <option value="">All sites (company-wide)</option>
                          {siteOptions(editForm.siteId).map((site) => (
                            <option key={site.id} value={String(site.id)}>
                              {site.name}
                              {site.is_active ? "" : " (inactive)"}
                            </option>
                          ))}
                        </select>
                        <label className="checkbox-field">
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                          />
                          Active
                        </label>
                        <input
                          type="password"
                          value={editForm.password}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, password: event.target.value }))}
                          placeholder="New password (optional)"
                          aria-label="New password"
                        />
                        <div className="action-cell">
                          <button
                            className="refresh-btn"
                            type="button"
                            disabled={editPending}
                            onClick={() => void submitEdit(user.id, user.username, user.is_active)}
                          >
                            {editPending ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="refresh-btn danger-action-btn"
                            type="button"
                            onClick={() => setEditingUserId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      {editError ? <p className="error-banner action-error">{editError}</p> : null}
                    </td>
                  </tr>
                ) : (
                  <tr key={`user-${user.id}`}>
                    <td>{user.username}</td>
                    <td>{user.email || "—"}</td>
                    <td>{user.role}</td>
                    <td>{siteName(user.site_id)}</td>
                    <td>
                      <span className={user.is_active ? "status-pill status-approved" : "status-pill status-rejected"}>
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="refresh-btn"
                        type="button"
                        onClick={() => startEdit(user.id, user.email ?? "", user.role, user.is_active, user.site_id)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} hasNext={hasNext} onPrev={prevPage} onNext={nextPage} disabled={loading} />
    </article>
  );
}
